import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import {
  MatLegacyTableDataSource as MatTableDataSource,
  MatLegacyTableModule as MatTableModule,
} from '@angular/material/legacy-table';
import { PageEvent } from '@angular/material/paginator';
import { ApiLinks } from '@cutting/app.api-links';
import {
  DirectivesModule,
  InfiniteScrollModule,
  NgxLocalizedNumbers,
  OverlayLoaderModule,
  PaginatedList,
  PriceList,
  PriceListProduct,
  ProductModelViewerDialogComponent,
} from 'cloud-shared-lib';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, map, share, startWith, switchMap, tap } from 'rxjs/operators';

import { AsyncPipe, NgIf } from '@angular/common';
import { OrderDetailService } from '../../../order-detail.service';

@Component({
  selector: 'cutting-price-list-products',
  templateUrl: './price-list-products.component.html',
  styleUrls: ['./price-list-products.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgxLocalizedNumbers,
    AsyncPipe,
    MatTableModule,
    OverlayLoaderModule,
    DirectivesModule,
    InfiniteScrollModule,
    ProductModelViewerDialogComponent,
  ],
})
export class PriceListProductsComponent {
  // selectedGroupId = 0;
  @Input() priceList: PriceList;
  @Output() selectedProductChange: EventEmitter<number> = new EventEmitter();

  displayedColumns: string[] = ['preview', 'name', 'price', 'article', 'width', 'height', 'length'];
  dataSource: MatTableDataSource<PriceListProduct> = new MatTableDataSource([]);

  data$: Observable<PaginatedList<PriceListProduct>>;
  loading$: Observable<boolean>;
  productModelViewerDialog: MatDialogRef<ProductModelViewerDialogComponent>;

  selectedGroupIdChange: Subject<number> = new Subject();

  initialPagination: Pick<PageEvent, 'pageIndex' | 'pageSize'> = {
    pageIndex: 0,
    pageSize: 30,
  };
  paginator: BehaviorSubject<Pick<PageEvent, 'pageIndex' | 'pageSize'>> = new BehaviorSubject(this.initialPagination);

  public filter: BehaviorSubject<string> = new BehaviorSubject('');

  @Input() set selectedGroupId(val: number) {
    this.selectedGroupIdChange.next(val);
  }

  constructor(private orderDetailService: OrderDetailService, public dialog: MatDialog) {
    this.data$ = combineLatest([
      this.selectedGroupIdChange.pipe(tap(() => this.reset())),
      this.filter.pipe(tap(() => this.reset())),
      this.paginator,
    ]).pipe(
      debounceTime(50),
      switchMap(([group, filter, paginator]) =>
        this.orderDetailService.getPriceListProducts(
          this.priceList.id,
          group,
          filter,
          paginator.pageIndex,
          paginator.pageSize
        )
      ),
      tap(p => (this.dataSource.data = [...this.dataSource.data, ...(p?.items ?? [])])),
      share()
    );

    this.loading$ = this.data$.pipe(
      startWith(null),
      map(d => !d?.items)
    );
  }

  reset(): void {
    this.paginator.next(this.initialPagination);
    this.dataSource.data = [];
  }

  update(groupId: number): void {
    this.selectedGroupIdChange.next(groupId);
  }

  selectProduct(productId: number): void {
    this.selectedProductChange.emit(productId);
  }

  getProductImagePath(productId: number): string {
    return `${ApiLinks.elasticModelPriceList}/${this.priceList.id}/${productId}/preview`;
  }

  onScroll(): void {
    this.paginator.next({ ...this.paginator.value, pageIndex: this.paginator.value.pageIndex + 1 });
  }
}
